import React, { FunctionComponent } from 'react';
import { SpeedAndInterfaceType, ApiResponse } from 'constants/api';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import ViewContainer from 'components/utils/ViewContainer';
import { Row, Col, Card, CardBody } from 'reactstrap';
import SyncButton from 'components/utils/SyncButton';
import CardHeader from 'reactstrap/lib/CardHeader';
import { DetailsTable } from 'components/styled';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { compose } from 'recompose';
import RequestStatusRenderer from 'components/utils/RequestStatusRenderer';
import PageLoading from 'components/utils/PageLoading';
import NotFound from './NotFound';
import AlertMessage from 'components/utils/AlertMessage';
import SpeedAndInterfaceTypeEditModal from 'components/modals/SpeedAndInterfaceTypeEditModal';

interface InProps {
    speedAndInterfaceType: SpeedAndInterfaceType;
    refetchData: () => void;
}

const SpeedAndInterfaceTypeDetails: FunctionComponent<InProps> = ({ speedAndInterfaceType, refetchData }) => {
    const { t } = useTranslation();

    const pageTitle = t('speedAndInterfaceTypeDetails.pageTitle', { name: speedAndInterfaceType.name })

    return (
        <>
            <Helmet title={pageTitle} />
            <ViewContainer>
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center">
                                <h2>{pageTitle}</h2>
                                <SyncButton
                                    className="ml-2"
                                    title={t('speedAndInterfaceTypeDetails.refreshButton')}
                                    onClick={refetchData}
                                />
                            </div>

                            <SpeedAndInterfaceTypeEditModal speedAndInterfaceType={speedAndInterfaceType} />
                        </div>

                        <hr />

                        <Card className="card-accent-primary">
                            <CardHeader>
                                <strong>{t('speedAndInterfaceTypeDetails.detailsTitle')}</strong>
                            </CardHeader>

                            <CardBody>
                                <DetailsTable size="sm" borderless>
                                    <tbody>
                                        <tr>
                                            <th>{t('speedAndInterfaceType.name')}</th>
                                            <td>{speedAndInterfaceType.name}</td>
                                        </tr>

                                        <tr>
                                            <th>{t('speedAndInterfaceType.speed')}</th>
                                            <td>{`${speedAndInterfaceType.speed/1000}G`}</td>
                                        </tr>

                                        <tr>
                                            <th>{t('speedAndInterfaceType.interface_type')}</th>
                                            <td>{speedAndInterfaceType.interface_type}</td>
                                        </tr>

                                        <tr>
                                            <th>{t('speedAndInterfaceType.fibre')}</th>
                                            <td>{speedAndInterfaceType.fibre}</td>
                                        </tr>
                                    </tbody>
                                </DetailsTable>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </ViewContainer>
        </>
    );
};

interface WrapperInProps {
    uuid: string;
}

interface WrapperConnectProps {
    speedAndInterfaceTypeBinding: ApiDataBinding<ApiResponse<SpeedAndInterfaceType>>;
}

type WrapperProps = WrapperInProps & WrapperConnectProps;

const enhance = compose<WrapperProps, WrapperInProps>(
    withApiData(
        {
            speedAndInterfaceTypeBinding: 'getSpeedAndInterfaceType'
        },
        (ownProps: WrapperInProps) => ({
            speedAndInterfaceTypeBinding: { uuid: ownProps.uuid }
        })
    )
);

const Wrapper: FunctionComponent<WrapperProps> = ({ speedAndInterfaceTypeBinding }) => {
    const { t } = useTranslation();

    return (
        <RequestStatusRenderer
            request={speedAndInterfaceTypeBinding}
            loading={<PageLoading />}
            success={() => (
                <SpeedAndInterfaceTypeDetails
                    speedAndInterfaceType={speedAndInterfaceTypeBinding.data!.data!}
                    refetchData={speedAndInterfaceTypeBinding.invalidateCache}
                />
            )}
            failed={() => {
                if (speedAndInterfaceTypeBinding.request.response!.status === 404) {
                    return <NotFound />;
                } else {
                    return <AlertMessage message={t('notification.unexpectedError')} />
                }
            }}
        />
    );
};

export default enhance(Wrapper);
